import { Copy } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { cn } from '@/lib/utils';

export interface TableColumn<T> {
  key: string;
  /** Header text */
  label: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (row: T, index: number) => ReactNode;
}

interface TableAffiliateProps<T> {
  title?: string;
  description?: string;
  columns: TableColumn<T>[];
  rows: T[];
  /** Stable key per row. Default: the row index */
  rowKey?: (row: T, index: number) => string;
  /** Rows visible before the list scrolls vertically. Default: 5 */
  visibleRows?: number;
  /** Extra minimum width; by default the columns' own widths decide when to scroll sideways */
  minWidth?: number;
  emptyText?: string;
  className?: string;
}

const ROW_HEIGHT = 56;
const HEAD_HEIGHT = 48;

const ALIGN = {
  left: 'text-left justify-start',
  center: 'text-center justify-center',
  right: 'text-right justify-end',
} as const;

const TableAffiliate = <T,>({
  title,
  description,
  columns,
  rows,
  rowKey,
  visibleRows = 5,
  minWidth,
  emptyText = 'Belum ada data.',
  className,
}: TableAffiliateProps<T>) => {
  // Same basis on every cell of a column so the header and rows line up;
  // every cell grows equally to fill the full width
  const cellStyle = (column: TableColumn<T>) => ({
    flex: '1 1 0%',
    minWidth: column.width,
  });

  const cellValue = (row: T, column: TableColumn<T>, index: number) => {
    if (column.render) return column.render(row, index);
    const value = (row as Record<string, unknown>)[column.key];
    return value == null ? '-' : String(value);
  };

  return (
    <article className={cn('shadowCard p-6 flex flex-col w-full gap-3', className)}>
      {title && (
        <div className="shrink-0">
          <p className="title2bold capitalize">{title}</p>
          {description && <p className="body2 text-text-grey">{description}</p>}
        </div>
      )}

      {/* Scrolls sideways on narrow screens and vertically past `visibleRows` */}
      <div
        className="w-full overflow-auto"
        style={{ maxHeight: HEAD_HEIGHT + ROW_HEIGHT * visibleRows }}
      >
        {/* Grows with the columns so the header background covers every cell */}
        <div className="w-max min-w-full" style={{ minWidth }}>
          <div
            className="flex items-center gap-3 px-3 sticky top-0 z-10 bg-white dark:bg-[var(--db-card)] body1bold text-black dark:text-white"
            style={{ height: HEAD_HEIGHT }}
          >
            {columns.map((column) => (
              <span
                key={column.key}
                className={cn('whitespace-nowrap', ALIGN[column.align ?? 'left'])}
                style={cellStyle(column)}
              >
                {column.label}
              </span>
            ))}
          </div>

          {rows.length === 0 ? (
            <p
              className="body1 text-text-grey text-center flex items-center justify-center"
              style={{ height: ROW_HEIGHT }}
            >
              {emptyText}
            </p>
          ) : (
            rows.map((row, index) => (
              <div
                key={rowKey?.(row, index) ?? index}
                className="flex items-center gap-3 px-3 body1 border-t border-blue-cs-200/60"
                style={{ height: ROW_HEIGHT }}
              >
                {columns.map((column) => (
                  <span
                    key={column.key}
                    className={cn(
                      'flex items-center whitespace-nowrap',
                      ALIGN[column.align ?? 'left']
                    )}
                    style={cellStyle(column)}
                  >
                    {cellValue(row, column, index)}
                  </span>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </article>
  );
};

interface CopyTextProps {
  value: string;
  label?: string;
}

/** Cell helper: text with a copy-to-clipboard button, for use in a column's `render` */
export const CopyText = ({ value, label = 'Salin' }: CopyTextProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(value).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <span className="inline-flex items-center gap-1">
      <span>{value}</span>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={label}
        className="shrink-0 inline-flex items-center text-text-blue-100 transition hover:opacity-80"
      >
        {copied ? 'Tersalin' : <Copy size={14} strokeWidth={1.8} />}
      </button>
    </span>
  );
};

export default TableAffiliate;
